<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 text-gray-900 relative overflow-hidden flex flex-col">
    
    <div class="absolute inset-0 opacity-5">
      <div class="absolute inset-0" style="background-image: radial-gradient(#94a3b8 1px, transparent 1px); background-size: 30px 30px;"></div>
    </div>

    <div class="relative z-10 flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div class="w-full max-w-md">

        <!-- Header -->
        <div class="text-center mb-4">
          <div class="inline-flex items-center gap-3 mb-2">
            <h1 class="text-3xl md:text-4xl font-bold text-gray-900">
              {{ $t('recaptchaGate.title') }}
              <span class="text-blue-600">{{ $t('recaptchaGate.titleHighlight') }}</span>
            </h1>
          </div>
          <p class="text-gray-600 text-lg">{{ $t('recaptchaGate.subtitle') }}</p>
        </div>

        <!-- Card -->
        <div class="bg-white/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-gray-200/50">

          <!-- ── WAITING STATE (user must check the box) ── -->
          <div v-if="status === 'waiting'" class="flex flex-col items-center text-center gap-6">
            <div>
              <p class="font-semibold text-gray-800 text-lg">{{ $t('recaptchaGate.instruction') }}</p>
              <p class="text-sm text-gray-500 mt-1">{{ $t('recaptchaGate.instructionDesc') }}</p>
            </div>
            <!-- The v2 checkbox renders here -->
            <div id="recaptcha-widget" class="flex justify-center"></div>
            <button @click="goBack"
              class="text-sm text-gray-400 hover:text-gray-600 underline-offset-2 transition-colors">
              {{ $t('recaptchaGate.goBack') }}
            </button>
          </div>

          <!-- ── VERIFYING STATE (after checkbox, calling backend) ── -->
          <div v-if="status === 'verifying'" class="flex flex-col items-center text-center gap-5">
            <div class="relative w-20 h-20">
              <svg class="absolute inset-0 w-20 h-20 animate-spin" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="34" stroke="#e0e7ff" stroke-width="6" />
                <path d="M74 40 a34 34 0 0 1 -34 34" stroke="#3b82f6" stroke-width="6" stroke-linecap="round" />
              </svg>
              <div class="absolute inset-0 flex items-center justify-center">
                <svg class="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
            <div>
              <p class="font-semibold text-gray-800 text-lg">{{ $t('recaptchaGate.verifying') }}</p>
              <p class="text-sm text-gray-500 mt-1">{{ $t('recaptchaGate.verifyingDesc') }}</p>
            </div>
          </div>

          <!-- ── SUCCESS STATE ── -->
          <div v-else-if="status === 'success'" class="flex flex-col items-center text-center gap-5">
            <div>
              <p class="font-semibold text-gray-800 text-lg">{{ $t('recaptchaGate.success') }}</p>
              <p class="text-sm text-gray-500 mt-1">{{ $t('recaptchaGate.successDesc') }}</p>
            </div>
            <!-- Progress bar auto-redirect -->
            <div class="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div class="h-1.5 bg-green-500 rounded-full transition-all duration-100 ease-linear"
                :style="{ width: redirectProgress + '%' }"></div>
            </div>
            <p class="text-xs text-gray-400">{{ $t('recaptchaGate.redirecting') }}</p>
          </div>

          <!-- ── ERROR STATE ── -->
          <div v-else-if="status === 'error'" class="flex flex-col items-center text-center gap-5">
            <div class="w-20 h-20 bg-gradient-to-br from-red-400 to-rose-600 rounded-full flex items-center justify-center shadow-lg shadow-red-500/30">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p class="font-semibold text-gray-800 text-lg">{{ $t('recaptchaGate.failed') }}</p>
              <p class="text-sm text-gray-500 mt-1">{{ errorMessage || $t('recaptchaGate.failedDesc') }}</p>
            </div>
            <div class="flex gap-3 w-full">
              <button @click="goBack"
                class="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
                {{ $t('recaptchaGate.goBack') }}
              </button>
              <button @click="retry"
                class="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all duration-200 shadow-md shadow-blue-500/20">
                {{ $t('recaptchaGate.retry') }}
              </button>
            </div>
          </div>

        </div>

        <!-- reCAPTCHA branding note -->
        <p class="text-center text-xs text-gray-400 mt-4">
          {{ $t('recaptchaGate.protected') }}
          <a href="https://policies.google.com/privacy" target="_blank" class="underline hover:text-gray-600">{{ $t('recaptchaGate.privacyPolicy') }}</a>
          {{ $t('recaptchaGate.and') }}
          <a href="https://policies.google.com/terms" target="_blank" class="underline hover:text-gray-600">{{ $t('recaptchaGate.termsOfService') }}</a>
          {{ $t('recaptchaGate.apply') }}
        </p>
      </div>
    </div>

    <!-- Footer (matches ReportTypeSelection) -->
    <div class="border-t border-gray-200 bg-white w-full">
      <div class="container mx-auto px-4 py-4">
        <div class="flex flex-col md:flex-row justify-between items-center gap-3">
          <p class="text-xs text-gray-600">&copy; 2024 ANTIC CIRT</p>
          <div class="flex items-center gap-4 text-xs text-gray-500">
            <a href="#" class="hover:text-gray-900 transition-colors">{{ $t('reportType.privacy') }}</a>
            <a href="#" class="hover:text-gray-900 transition-colors">{{ $t('reportType.terms') }}</a>
            <a href="#" class="hover:text-gray-900 transition-colors">{{ $t('reportType.help') }}</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadRecaptcha, renderRecaptcha, resetRecaptcha } from '@/utils/recaptcha.js';

export default {
  name: 'RecaptchaGate',
  data() {
    return {
      status: 'waiting',     // 'waiting' | 'verifying' | 'success' | 'error'
      widgetId: null,
      errorMessage: '',
      redirectProgress: 0,
      redirectTimer: null,
    };
  },
  computed: {
    reportType() {
      return this.$route.params.type;
    },
    currentLang() {
      return this.$route.params.lang || this.$i18n.locale || 'en';
    },
  },
  async mounted() {
    try {
      await loadRecaptcha();
      await this.$nextTick(); // ensure #recaptcha-widget is in the DOM
      this.renderWidget();
    } catch (err) {
      this.status = 'error';
      this.errorMessage = 'Could not load reCAPTCHA. Check your connection.';
    }
  },
  beforeUnmount() {
    if (this.redirectTimer) clearInterval(this.redirectTimer);
  },
  methods: {
    async renderWidget() {
      const container = document.getElementById('recaptcha-widget');
      if (!container) return;
      // Clear any previous render to avoid duplicate widget timeout
      container.innerHTML = '';
      this.widgetId = null;
      try {
        this.widgetId = await renderRecaptcha(
          'recaptcha-widget',
          this.onHumanConfirmed,
          this.onTokenExpired,
        );
      } catch (err) {
        this.status = 'error';
        this.errorMessage = 'Could not render reCAPTCHA. Please refresh the page.';
      }
    },

    onHumanConfirmed(token) {
      // Do NOT verify here: a v2 token is single-use, and verifying would
      // consume it. Carry the raw token to the form, where /start-session/
      // performs the single server-side verification.
      if (!token) {
        this.status = 'error';
        this.errorMessage = '';
        resetRecaptcha(this.widgetId);
        return;
      }
      try {
        sessionStorage.setItem('recaptcha_token', token);
      } catch (e) {
        // sessionStorage unavailable; form will fall back to its own checkbox.
      }
      this.status = 'success';
      this.startRedirect();
    },

    onTokenExpired() {
      this.status = 'waiting';
    },

    startRedirect() {
      this.redirectProgress = 0;
      const totalMs = 1500;
      const intervalMs = 30;
      const step = (intervalMs / totalMs) * 100;
      this.redirectTimer = setInterval(() => {
        this.redirectProgress = Math.min(100, this.redirectProgress + step);
        if (this.redirectProgress >= 100) {
          clearInterval(this.redirectTimer);
          this.navigateToForm();
        }
      }, intervalMs);
    },

    navigateToForm() {
      const lang = this.currentLang;
      const routeMap = {
        'cyber':     'cyber-incident',
        'complaint': 'antic-complaint',
        'followup':  'followup',
      };
      const routeName = routeMap[this.reportType];
      if (routeName) {
        this.$router.push({ name: routeName, params: { lang } });
      }
    },

    async retry() {
      this.status = 'waiting';
      this.errorMessage = '';
      await this.$nextTick();
      this.renderWidget();
    },

    goBack() {
      this.$router.push({ name: 'report-incident', params: { lang: this.currentLang } });
    },
  },
};
</script>

<style scoped>
@keyframes bounce-once {
  0%, 100% { transform: translateY(0); }
  30% { transform: translateY(-12px); }
  60% { transform: translateY(-6px); }
}
.animate-bounce-once {
  animation: bounce-once 0.6s ease-out;
}
</style>